import { Heart, Check, Loader2 } from "lucide-react";
import { followOnSpotify } from "../lib/spotify";
import { useAppleMusicFavorite } from "../hooks/useAppleMusicFavorite";
import { Reveal } from "./Reveal";
import { SpotifyIcon } from "./icons/BrandIcons";

// Renders in two modes: a full homepage/release-page section, or a compact row for the sticky engagement CTA.
export default function FollowFavorite({
  settings,
  compact = false,
  title = "Follow DJ Código",
  subtitle = "Follow DJ Código on Spotify and catch every new release.",
}) {
  const spotifyUrl = settings?.streaming?.spotify;
  const appleMusicUrl = settings?.streaming?.apple_music;
  const { status, favorite } = useAppleMusicFavorite(appleMusicUrl);

  if (!spotifyUrl && !appleMusicUrl) return null;

  const openSpotifyFollow = () => followOnSpotify(spotifyUrl);

  const appleLabel =
    status === "favorited" ? (
      <>
        <Heart size={compact ? 12 : 16} fill="currentColor" /> Favorited
      </>
    ) : status === "authorizing" ? (
      <>
        <Loader2 size={compact ? 12 : 16} className="animate-spin" /> Connecting…
      </>
    ) : (
      <>
        <Heart size={compact ? 12 : 16} /> {compact ? "Favorite" : "Apple Music — Favorite"}
      </>
    );

  if (compact) {
    return (
      <div data-testid="follow-favorite-compact" className="mt-3 flex items-center gap-2 border-t border-white/10 pt-3">
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-white/40">Follow</span>
        {spotifyUrl && (
          <button
            data-testid="compact-spotify-follow"
            onClick={openSpotifyFollow}
            className="flex-1 rounded-full border border-[#1DB954]/40 bg-[#1DB954]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[#1DB954] transition-transform active:scale-95"
          >
            <span className="inline-flex items-center justify-center gap-1"><SpotifyIcon size={12} /> Spotify — Follow</span>
          </button>
        )}
        {appleMusicUrl && (
          <button
            data-testid="compact-apple-favorite"
            onClick={favorite}
            disabled={status === "authorizing"}
            className="flex flex-1 items-center justify-center gap-1 rounded-full border border-[#FA57C1]/40 bg-[#FA57C1]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[#FA57C1] transition-transform active:scale-95 disabled:opacity-60"
          >
            {appleLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <section data-testid="follow-favorite-section" className="px-6 py-10 sm:px-10 lg:px-16">
      <Reveal>
        <div className="mx-auto max-w-3xl rounded-3xl border border-gold/20 bg-surface/50 p-6 text-center sm:p-8">
          <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white">{title}</h3>
          <p className="mt-2 text-sm text-white/50">{subtitle}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {spotifyUrl && (
              <button
                data-testid="spotify-follow-button"
                onClick={openSpotifyFollow}
                className="inline-flex items-center gap-2 rounded-full border border-[#1DB954]/50 bg-[#1DB954]/10 px-6 py-3 text-sm font-bold uppercase tracking-wide text-[#1DB954] transition-transform active:scale-95 hover:bg-[#1DB954]/20"
              >
                <SpotifyIcon size={18} /> Spotify — Follow
              </button>
            )}
            {appleMusicUrl && (
              <button
                data-testid="apple-music-favorite-button"
                onClick={favorite}
                disabled={status === "authorizing"}
                className="inline-flex items-center gap-2 rounded-full border border-[#FA57C1]/50 bg-[#FA57C1]/10 px-6 py-3 text-sm font-bold uppercase tracking-wide text-[#FA57C1] transition-transform active:scale-95 hover:bg-[#FA57C1]/20 disabled:opacity-60"
              >
                {appleLabel}
              </button>
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
}
